import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import Text from '@/components/Typography/Text';
import { downloadPlaybackAudio } from '@/lib/readAloudPlayback';

// Native player construction/status hooks can throw before an effect or play
// callback can catch the failure. Keep that failure inside read aloud so it
// cannot replace the entire chat with the application's crash screen.
class PlaybackBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) {
    console.error('[ReadAloud] Audio player failed:', error);
    this.props.onError?.(error);
  }
  render() { return this.state.failed ? null : this.props.children; }
}

const styles = StyleSheet.create({
  queue: {
    alignItems: 'center',
    width: '100%',
  },
  player: {
    height: 92,
    width: '100%',
  },
  segmentControls: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  segmentButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 16,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  segmentButtonDisabled: {
    opacity: 0.3,
  },
});

function normalizeSources(chunks, fallbackUrl) {
  const byIndex = new Map();
  if (Array.isArray(chunks)) {
    chunks.forEach((chunk) => {
      if (!Number.isInteger(chunk?.index) || chunk.index < 0 || !chunk?.url) return;
      byIndex.set(chunk.index, {
        index: chunk.index,
        url: chunk.url,
        contentType: chunk.contentType || 'audio/mpeg',
      });
    });
  }

  const ordered = Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
  if (ordered.length > 0) return ordered;
  return fallbackUrl
    ? [{ index: 0, url: fallbackUrl, contentType: 'audio/mpeg' }]
    : [];
}

const MessageSpeakAudioQueue = forwardRef(function MessageSpeakAudioQueue({
  chunks = [],
  fallbackUrl = null,
  isGenerationComplete = false,
  size = 320,
  persistentPlayback = false,
  suspended = false,
  onPlaybackStateChange,
  onPlaybackError,
  onPlaybackStarted,
}, ref) {
  const sources = useMemo(
    () => normalizeSources(chunks, fallbackUrl),
    [chunks, fallbackUrl]
  );
  const [activeIndex, setActiveIndex] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [combinedObjectUrl, setCombinedObjectUrl] = useState(null);
  const [playbackMode, setPlaybackMode] = useState('chunks');
  const [combinedStartPosition, setCombinedStartPosition] = useState(0);
  const playerRef = useRef(null);
  const lastPlayerRef = useRef(null);
  const chunkDurationsRef = useRef(new Map());
  const [localSources, setLocalSources] = useState({});
  const fallbackAttempts = useRef(new Set());
  const pendingFallbacks = useRef(new Set());
  const localFiles = useRef([]);
  const mounted = useRef(true);
  const downloadAbort = useRef(null);
  if (!downloadAbort.current) downloadAbort.current = new AbortController();
  useEffect(() => {
    mounted.current = true;
    if (downloadAbort.current.signal.aborted) downloadAbort.current = new AbortController();
    return () => {
      mounted.current = false;
      downloadAbort.current?.abort();
      localFiles.current.forEach(file => file.dispose());
    };
  }, []);
  const setPlayerRef = useCallback((instance) => {
    playerRef.current = instance;
    if (instance) lastPlayerRef.current = instance;
  }, []);

  const combinedUrl = fallbackUrl || combinedObjectUrl;
  const activeSource = playbackMode === 'combined' && combinedUrl
    ? { index: 'combined', url: combinedUrl, contentType: 'audio/mpeg' }
    : sources.find(source => source.index === activeIndex) || null;
  const nextSource = sources.find(source => source.index === (activeIndex ?? -1) + 1) || null;
  const sourceUrl = activeSource?.url;
  const activeSourceUrlRef = useRef(sourceUrl);
  activeSourceUrlRef.current = sourceUrl;
  const playbackSource = localSources[sourceUrl] || sourceUrl;
  const handlePlaybackStateChange = useCallback(state => {
    onPlaybackStateChange?.(pendingFallbacks.current.has(sourceUrl)
      ? { ...state, playing: false, available: false, loading: true } : state);
  }, [onPlaybackStateChange, sourceUrl]);
  const handlePlaybackError = useCallback(async error => {
    if (pendingFallbacks.current.has(sourceUrl)) return;
    if (Platform.OS !== 'android' || !sourceUrl || fallbackAttempts.current.has(sourceUrl)) {
      const detail = new Error(error?.message || 'Audio playback failed');
      detail.stack = error?.stack || detail.stack;
      detail.errorCode = error?.errorCode;
      detail.playbackStage = localSources[sourceUrl] ? 'android_media_player' : 'remote_player';
      onPlaybackError?.(detail);
      return;
    }
    fallbackAttempts.current.add(sourceUrl);
    pendingFallbacks.current.add(sourceUrl);
    onPlaybackStateChange?.({ playing: false, available: false, loading: true });
    try {
      const file = await downloadPlaybackAudio(sourceUrl, downloadAbort.current?.signal);
      pendingFallbacks.current.delete(sourceUrl);
      if (!mounted.current) { await file.dispose(); return; }
      localFiles.current.push(file);
      setLocalSources(previous => ({ ...previous, [sourceUrl]: file.uri }));
    } catch (fallbackError) {
      pendingFallbacks.current.delete(sourceUrl);
      if (!mounted.current || activeSourceUrlRef.current !== sourceUrl) return;
      const detail = new Error(`${error?.message || 'Audio playback failed'}; ${fallbackError.message}`);
      detail.stack = `${error?.stack || ''}\n${fallbackError.stack || ''}`;
      detail.errorCode = fallbackError.errorCode || error?.errorCode;
      detail.playbackStage = 'audio_download';
      onPlaybackError?.(detail);
    }
  }, [localSources, onPlaybackError, onPlaybackStateChange, sourceUrl]);

  // Legacy cache entries only retained the MP3 chunks. Assemble those bytes in
  // the browser once generation is complete so the user still gets one fully
  // scrubbable track.
  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !isGenerationComplete ||
      fallbackUrl ||
      sources.length < 2
    ) return undefined;

    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const responses = await Promise.all(sources.map(source => fetch(source.url, {
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
        })));
        if (responses.some(response => !response.ok)) return;
        const parts = await Promise.all(responses.map(response => response.arrayBuffer()));
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob(parts, { type: 'audio/mpeg' }));
        setCombinedObjectUrl(objectUrl);
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fallbackUrl, isGenerationComplete, sources]);

  useEffect(() => {
    if (!isGenerationComplete || !combinedUrl || playbackMode === 'combined') return;
    const elapsedBeforeActive = sources
      .filter(source => source.index < (activeIndex ?? 0))
      .reduce((total, source) => total + (chunkDurationsRef.current.get(source.index) || 0), 0);
    setCombinedStartPosition(elapsedBeforeActive + (playerRef.current?.getPosition?.() || 0));
    setPlaybackMode('combined');
  }, [activeIndex, combinedUrl, isGenerationComplete, playbackMode, sources]);

  // Chunk zero is the only valid start point. This avoids starting midway
  // through a response if a poll temporarily returns later chunks first.
  useEffect(() => {
    if (activeIndex === null && sources.some(source => source.index === 0)) {
      setActiveIndex(0);
      setShouldAutoPlay(!suspended);
    }
  }, [activeIndex, sources, suspended]);

  // A chunk can finish before the following poll discovers its successor.
  // Resume immediately when that exact next chunk appears.
  useEffect(() => {
    if (!waitingForNext || !nextSource || !shouldAutoPlay) return;
    setWaitingForNext(false);
    setActiveIndex(nextSource.index);
  }, [nextSource, shouldAutoPlay, waitingForNext]);

  const handlePlaybackEnd = useCallback(() => {
    if (playbackMode === 'combined') {
      setShouldAutoPlay(false);
      return;
    }
    const activeDuration = playerRef.current?.getDuration?.() || 0;
    if (activeIndex !== null && activeDuration > 0) {
      chunkDurationsRef.current.set(activeIndex, activeDuration);
    }
    if (nextSource) {
      setActiveIndex(nextSource.index);
      return;
    }
    if (isGenerationComplete) {
      setShouldAutoPlay(false);
      setWaitingForNext(false);
      setActiveIndex(0);
      return;
    }
    setWaitingForNext(true);
  }, [activeIndex, isGenerationComplete, nextSource, playbackMode]);
  const handlePlaybackStart = useCallback(() => {
    setShouldAutoPlay(true);
    onPlaybackStarted?.();
  }, [onPlaybackStarted]);
  const handlePlaybackPause = useCallback(() => {
    setShouldAutoPlay(false);
  }, []);
  const jumpToSegment = useCallback((index) => {
    setWaitingForNext(false);
    setShouldAutoPlay(true);
    setActiveIndex(index);
  }, []);

  useImperativeHandle(ref, () => ({
    play() {
      if (suspended) return;
      setShouldAutoPlay(true);
      if (waitingForNext && nextSource) {
        setWaitingForNext(false);
        setActiveIndex(nextSource.index);
      } else {
        playerRef.current?.play?.();
      }
    },
    pause() {
      setShouldAutoPlay(false);
      playerRef.current?.pause?.();
    },
    stop() {
      setShouldAutoPlay(false);
      setWaitingForNext(false);
      playerRef.current?.stop?.();
    },
    isPlaying() {
      return playerRef.current?.isPlaying?.() || false;
    },
  }), [nextSource, suspended, waitingForNext]);

  useEffect(() => () => {
    try { lastPlayerRef.current?.stop?.(); } catch (_) {}
  }, []);

  useEffect(() => {
    if (suspended) {
      setShouldAutoPlay(false);
      playerRef.current?.pause?.();
    }
  }, [suspended]);

  if (!activeSource) return null;

  const activePosition = sources.findIndex(source => source.index === activeIndex);
  const previousSource = activePosition > 0 ? sources[activePosition - 1] : null;
  const followingSource = activePosition >= 0 ? sources[activePosition + 1] : null;

  return (
    <View style={styles.queue}>
      <View style={styles.player}>
        <PlaybackBoundary key={`${activeSource.index}:${playbackSource}`} onError={handlePlaybackError}>
          <AudioWaveformPlayer
            ref={setPlayerRef}
            sourceUrl={playbackSource}
            androidMediaPlayer={Platform.OS === 'android' && !!localSources[sourceUrl]}
            file={{
              url: activeSource.url,
              cdnPath: activeSource.url,
              name: `message-speak-${activeSource.index}.mp3`,
              type: activeSource.contentType,
            }}
            size={size}
            autoPlay={shouldAutoPlay && !suspended}
            backgroundPlayback={persistentPlayback}
            suspended={suspended}
            deliberateSeek={persistentPlayback}
            initialPositionSeconds={playbackMode === 'combined' ? combinedStartPosition : 0}
            playbackRate={playbackRate}
            onPlaybackRateChange={setPlaybackRate}
            onPlaybackStart={handlePlaybackStart}
            onPlaybackPause={handlePlaybackPause}
            onPlaybackStateChange={handlePlaybackStateChange}
            onPlaybackError={handlePlaybackError}
            onPlaybackEnd={handlePlaybackEnd}
          />
        </PlaybackBoundary>
      </View>
      {playbackMode === 'chunks' && sources.length > 1 && (
        <View style={styles.segmentControls}>
          <Pressable
            style={[styles.segmentButton, !previousSource && styles.segmentButtonDisabled]}
            disabled={!previousSource}
            onPress={() => previousSource && jumpToSegment(previousSource.index)}
            accessibilityLabel="Previous audio segment"
          >
            <Text>‹</Text>
          </Pressable>
          <Text size="xs">
            Part {activePosition + 1} of {sources.length}
          </Text>
          <Pressable
            style={[styles.segmentButton, !followingSource && styles.segmentButtonDisabled]}
            disabled={!followingSource}
            onPress={() => followingSource && jumpToSegment(followingSource.index)}
            accessibilityLabel="Next audio segment"
          >
            <Text>›</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
});

export { normalizeSources };
export default MessageSpeakAudioQueue;
